import {
  SIDEBAR_STATE,
  SIDEBAR_LOCALSTORAGE_KEY,
  DEFAULT_SIDEBAR_STATE,
} from '../constants';

import {
  CREATE_ALERT_SUCCESS,
  REMOVE_ALERT_ERROR,
  REMOVE_ALERT_REQUEST,
  REMOVE_ALERT_SUCCESS,
  RESTORE_ALERT_SUCCESS,
  SORT_ALERTS,
  OPEN_ALERTS,
  CLOSE_ALERTS,
} from '../actions/alert';

import {
  ADD_LIST_ITEM_SUCCESS,
  REMOVE_LIST_ITEM_ERROR,
  REMOVE_LIST_ITEM_REQUEST,
  REMOVE_LIST_ITEM_SUCCESS,
  RESTORE_LIST_ITEM_SUCCESS,
  REMOVE_LIST_ERROR,
  REMOVE_LIST_REQUEST,
  REMOVE_LIST_SUCCESS,
  RESTORE_LIST_SUCCESS,
  CREATE_LIST_SUCCESS,
  RENAME_LIST_SUCCESS,
  SORT_LIST,
  SELECT_LIST,
  OPEN_LISTS,
  CLOSE_LISTS,
  ORDER_LISTS_REQUEST,
  ORDER_LIST_ITEMS_REQUEST,
} from '../actions/lists';

import { TOGGLE_FILTER } from '../actions/filter';

export default (state, action) => {
  if (!state) {
    return {
      tab: SIDEBAR_STATE.NONE,
      previousTab: null,
      error: null,
      pending: false,
      deletedListItems: {},
      deletedLists: {},
      tracking: {},
      alerts: {
        sort: 'price_change',
        direction: 'desc',
        products: [],
      },
      selectedList: null,
      lists: {},
      listRefresh: 0,
      hasFilter: false,
      filterType: null,
      filterGroups: [],
    };
  }

  switch (action.type) {
    case '__DANGEROUS_CONFIG_RECEIVE': {
      return {
        ...state,
        tracking: {
          settings: action.market.trackingToggles.reduce(
            (settings, setting) => ({
              ...settings,
              [setting.name]: setting.default,
            }),
            []
          ),
        },
      };
    }

    case 'SESSION_RECEIVE': {
      return {
        ...state,
        tracking: {
          ...state.tracking,
          settings: {
            ...state.tracking.settings,
            ...Object.keys(action.privacyCookie || {}).reduce(
              (settings, curr) =>
                Object.assign(settings, {
                  [curr]: Boolean(action.privacyCookie[curr]),
                }),
              {}
            ),
          },
        },
        personalization: {
          clientId: action.clientId,
          pulseEnvironmentId: action.pulseEnvironmentId,
        },
      };
    }

    case 'USER_RECEIVE': {
      return {
        ...state,
        alerts: {
          ...state.alerts,
          products:
            (action.me &&
              action.me.alertsSummary &&
              action.me.alertsSummary.products) ||
            [],
        },
      };
    }

    case 'LOGOUT_COMPLETE': {
      return {
        ...state,
        alerts: {
          ...state.alerts,
          products: [],
        },
      };
    }

    case 'ROUTE_LOADED': {
      let hasFilter = false;
      let filterSlice = null;
      if (action.data && action.data.productCollection) {
        filterSlice = action.data.productCollection.slices.find(
          slice => slice.__typename === 'FilterSlice'
        );
        hasFilter = !!filterSlice;
      }
      if (action.route?.name === 'category') {
        hasFilter = !!action.data?.category?.immediateProducts;
      }

      // Close filter when navigating away from a leaf category page
      if (state.tab === SIDEBAR_STATE.FILTER && !hasFilter) {
        return {
          ...state,
          tab: SIDEBAR_STATE.NONE,
          hasFilter: false,
          filterType: null,
          filterGroups: [],
        };
      }

      return {
        ...state,
        hasFilter,
        filterType: action.route?.name,
        filterGroups: filterSlice ? filterSlice.groups : [],
      };
    }

    case 'LOCAL_STORAGE_RECEIVE': {
      const sidebarStateFromLocalStorage =
        action.localStorageState[SIDEBAR_LOCALSTORAGE_KEY];

      if (sidebarStateFromLocalStorage) {
        const [sort, direction] = sidebarStateFromLocalStorage.listSort
          ? sidebarStateFromLocalStorage.listSort.split(':')
          : [];

        const shouldOpenDefaultTab =
          sidebarStateFromLocalStorage.tab === SIDEBAR_STATE.FILTER &&
          !state.hasFilter;

        return {
          ...state,
          lists: {
            ...state.lists,
            sort,
            direction,
          },
          tab: shouldOpenDefaultTab
            ? DEFAULT_SIDEBAR_STATE
            : sidebarStateFromLocalStorage.tab,
        };
      }

      return state;
    }

    case CLOSE_ALERTS: {
      return {
        ...state,
        tab: SIDEBAR_STATE.NONE,
      };
    }

    case OPEN_ALERTS: {
      return {
        ...state,
        tab: SIDEBAR_STATE.ALERTS,
        previousTab: SIDEBAR_STATE.ALERTS,
      };
    }

    case CLOSE_LISTS: {
      return {
        ...state,
        tab: SIDEBAR_STATE.NONE,
      };
    }

    case OPEN_LISTS: {
      return {
        ...state,
        tab: SIDEBAR_STATE.LISTS,
        previousTab: SIDEBAR_STATE.LISTS,
      };
    }

    case TOGGLE_FILTER: {
      if (state.tab === SIDEBAR_STATE.FILTER) {
        return {
          ...state,
          tab: SIDEBAR_STATE.NONE,
        };
      }

      return {
        ...state,
        tab: SIDEBAR_STATE.FILTER,
        previousTab: SIDEBAR_STATE.FILTER,
      };
    }

    case CREATE_ALERT_SUCCESS: {
      return {
        ...state,
        alerts: {
          ...state.alerts,
          products: [
            ...new Set([
              // Since an alert may already exist on current product with
              // a different alert ID clear all alerts for this product
              // and add the newly created alert
              ...state.alerts.products.filter(
                alert => alert.productId !== action.productId
              ),
              { alertId: action.id, productId: action.productId },
            ]),
          ],
        },
      };
    }

    case REMOVE_ALERT_REQUEST: {
      return {
        ...state,
        pending: true,
      };
    }

    case RESTORE_ALERT_SUCCESS: {
      return {
        ...state,
        alerts: {
          ...state.alerts,
          products: [
            ...new Set([
              // Since an alert may already exist on current product with
              // a different alert ID clear all alerts for this product
              // and add the newly created alert
              ...state.alerts.products.filter(
                alert => alert.productId !== action.productId
              ),
              { alertId: action.id, productId: action.productId },
            ]),
          ],
        },
      };
    }

    case REMOVE_ALERT_SUCCESS: {
      return {
        ...state,
        alerts: {
          ...state.alerts,
          products: state.alerts.products.filter(
            ({ alertId, productId }) =>
              alertId !== action.id && productId !== action.productId
          ),
        },
      };
    }

    case REMOVE_ALERT_ERROR: {
      return {
        ...state,
        pending: false,
        error: action.error,
      };
    }

    case SORT_LIST: {
      const [sort, direction] = action.sort.value.split(':');

      return {
        ...state,
        lists: {
          ...state.lists,
          sort,
          direction,
        },
      };
    }

    case SORT_ALERTS: {
      const [sort, direction] = action.sort.value.split(':');

      return {
        ...state,
        alerts: {
          ...state.alerts,
          sort,
          direction,
        },
      };
    }

    case SELECT_LIST: {
      return {
        ...state,
        selectedList: action.id,
      };
    }

    case REMOVE_LIST_ITEM_REQUEST: {
      return {
        ...state,
        pending: true,
        deletedListItems: {
          ...state.deletedListItems,
          [action.id]: 'pending',
        },
      };
    }

    case RESTORE_LIST_ITEM_SUCCESS: {
      return {
        ...state,
        deletedListItems: {
          ...state.deletedListItems,
          [action.id]: undefined,
        },
      };
    }

    case REMOVE_LIST_ITEM_SUCCESS: {
      return {
        ...state,
        listRefresh: state.listRefresh + 1,
        deletedListItems: {
          ...state.deletedListItems,
          [action.id]: action.productId || action.listId,
        },
        pending: false,
      };
    }

    case REMOVE_LIST_ITEM_ERROR: {
      return {
        ...state,
        pending: false,
        deletedListItems: {
          ...state.deletedListItems,
          [action.id]: undefined,
        },
      };
    }

    case REMOVE_LIST_REQUEST: {
      return {
        ...state,
        pending: true,
        deletedLists: {
          ...state.deletedLists,
          [action.id]: 'pending',
        },
      };
    }

    case RESTORE_LIST_SUCCESS: {
      return {
        ...state,
        listRefresh: state.listRefresh + 1,
        deletedLists: {
          ...state.deletedLists,
          [action.id]: undefined,
        },
      };
    }

    case REMOVE_LIST_SUCCESS: {
      return {
        ...state,
        listRefresh: state.listRefresh + 1,
        deletedLists: {
          ...state.deletedLists,
          [action.id]: true,
        },
        pending: false,
      };
    }

    case REMOVE_LIST_ERROR: {
      return {
        ...state,
        pending: false,
        listRefresh: state.listRefresh + 1,
        deletedLists: {
          ...state.deletedLists,
          [action.id]: undefined,
        },
      };
    }

    case CREATE_LIST_SUCCESS: {
      return {
        ...state,
        listRefresh: state.listRefresh + 1,
      };
    }

    case RENAME_LIST_SUCCESS: {
      return {
        ...state,
        listRefresh: state.listRefresh + 1, // Update UI when renaming list
      };
    }

    case ADD_LIST_ITEM_SUCCESS: {
      return {
        ...state,
        listRefresh: state.listRefresh + 1,
      };
    }

    case ORDER_LISTS_REQUEST: {
      return {
        ...state,
      };
    }

    case ORDER_LIST_ITEMS_REQUEST: {
      return {
        ...state,
      };
    }

    default:
      break;
  }

  return state;
};
